import React from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { useRef, useEffect } from 'react';
import '../styles/landing.css';
import backgroundImage from '../images/2.png';
import HomeSVG from '../images/teamgame.svg';
import { FaLinkedin, FaInstagram, FaDiscord, FaTwitter } from 'react-icons/fa';

const GlitchText = ({ children, className }) => {
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref);

  useEffect(() => {
    if (inView) {
      const glitchSequence = async () => {
        await controls.start({
          textShadow: [
            '3px 3px 5px rgba(0, 0, 0, 0.7)',
            '-3px -3px 5px rgba(0, 0, 0, 0.7)',
            '0px 0px 10px rgba(0, 0, 0, 0.5)'
          ],
          transition: { 
            duration: 0.3,
            repeat: 3,
            repeatType: "reverse"
          }
        });
      };

      glitchSequence();
    }
  }, [inView, controls]);

  return (
    <motion.div 
      ref={ref}
      animate={controls}
      className={className}
      style={{ 
        display: 'inline-block', 
        transformOrigin: 'center',
        willChange: 'transform, text-shadow'
      }}
    >
      {children}
    </motion.div>
  );
};

const AnimatedText = ({ text, className, baseDelay = 0, customStyles = {}, isOrange = false }) => {
  return (
    <div className={className}>
      {text.split("").map((char, index) => (
        <motion.span
          key={index}
          initial={{ 
            opacity: 0, 
            x: -50,
            rotate: -10
          }}
          animate={{ 
            opacity: 1, 
            x: 0,
            rotate: 0
          }}
          transition={{
            delay: baseDelay + index * 0.05,
            type: "spring",
            stiffness: 100,
            damping: 10
          }}
          style={{
            display: 'inline-block',
            willChange: 'transform, opacity',
            margin: char === ' ' ? '0 5px' : '0', 
            color: isOrange ? '#FF6B35' : 'inherit',
            ...customStyles
          }}
          className={`char-${index + 1}`}
        >
          {char}
        </motion.span>
      ))}
    </div>
  );
};

const Landing = () => {
  const titleWords = ["NEXOLVE"," ", "TECHNOLOGIES", " ","INDIA"];
  const subTagline = "Where creativity meets technology!";

  const buttonVariants = {
    hover: {
      scale: 1.1,
      boxShadow: "0px 0px 15px rgba(255, 255, 255, 0.6)",
      transition: { 
        type: "spring", 
        stiffness: 300 
      }
    },
    tap: { scale: 0.95 }
  };

  const socialIconVariants = {
    initial: { 
      opacity: 0, 
      scale: 0.5,
      y: 50 
    },
    animate: (index) => ({
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 10,
        delay: 0.5 + index * 0.2
      }
    }),
    hover: {
      scale: 1.25,
      rotate: [0, 15, -15, 0],
      transition: {
        type: "spring",
        stiffness: 300
      }
    },
    tap: {
      scale: 0.9
    }
  };

  const svgVariants = {
    initial: { 
      opacity: 0, 
      scale: 0.8,
      y: 50 
    },
    animate: {
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        duration: 1,
        type: "spring",
        stiffness: 50
      }
    },
    hover: {
      rotate: [0, 5, -5, 0],
      scale: [1, 1.05, 0.95, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "mirror"
      }
    }
  };

  const handleBookAppointment = () => {
    window.location.href = "https://calendly.com/maitreyakulkarni95/30min";
  };

  return (
    <div 
      className="landing"
      style={{ 
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="landing-content">
        <div className="title-container">
          {titleWords.map((word, index) => (
            <React.Fragment key={index}>
              <GlitchText className="title-line">
                <AnimatedText 
                  text={word}
                  className="main-title"
                  baseDelay={0.2 * (index + 1)}
                  isOrange={word === "NEXOLVE"}
                  customStyles={{
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 700
                  }}
                />
              </GlitchText>
              {index < titleWords.length - 1 && <span className="title-spacer"> </span>}
            </React.Fragment>
          ))}
        </div>

        <GlitchText className="sub-tagline-container">
          <AnimatedText 
            text={subTagline}
            className="tagline"
            baseDelay={0.4}
            customStyles={{
              fontFamily: '"Poppins", sans-serif'
            }}
          />
        </GlitchText>

        <motion.button
          className="appointment-button"
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
          initial={{ opacity: 0, y: 20 }}
          animate={{ 
            opacity: 1, 
            y: 0,
            transition: { 
              type: "spring", 
              delay: 1 
            } 
          }}
          onClick={handleBookAppointment}
        >
          Book an Appointment
        </motion.button>

        <div className="social-icons">
          {[
            { Icon: FaLinkedin, link: "https://www.linkedin.com/company/nexolvetechnologiesindia/" },
            { Icon: FaInstagram, link: "https://www.instagram.com/nexolvetechnologiesindia/" },
            { Icon: FaDiscord, link: "https://discord.com/invite/AaaB9wbPgu" },
            { Icon: FaTwitter, link: "#" }
          ].map(({ Icon, link }, index) => (
            <motion.a
              key={index}
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              variants={socialIconVariants}
              initial="initial"
              animate="animate"
              custom={index}
              whileHover="hover"
              whileTap="tap"
            >
              <Icon />
            </motion.a>
          ))}
        </div>
      </div>

      <motion.div
        className="svg-container"
        variants={svgVariants}
        initial="initial"
        animate="animate"
        whileHover="hover"
      >
        <img src={HomeSVG} alt="Home Illustration" className="home-svg" />
      </motion.div>
    </div>
  );
};

export default Landing;